/**
 * Auto-match file headers to canonical import fields (exact, then folded aliases).
 * First field in `fields` wins when two targets want the same source column.
 */
export type SuggestColumnField = {
  key: string;
  required?: boolean | 'subject';
  aliases?: readonly string[];
};

export type ImportColumnField = SuggestColumnField & {
  label: string;
};

export type ImportInspectResult = {
  headers: string[];
  sampleRows: Array<Record<string, string>>;
};

export const foldImportHeader = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');

export const suggestColumnMapping = (
  fileHeaders: string[],
  fields: readonly SuggestColumnField[]
): Record<string, string> => {
  const used = new Set<string>();
  const mapping: Record<string, string> = {};

  for (const field of fields) {
    const exact = fileHeaders.find((header) => header === field.key && !used.has(header));
    if (!exact) continue;
    mapping[field.key] = exact;
    used.add(exact);
  }

  for (const field of fields) {
    if (mapping[field.key]) continue;
    const candidates = new Set(
      [field.key, ...(field.aliases ?? [])].map((alias) => foldImportHeader(alias))
    );
    const match = fileHeaders.find(
      (header) => !used.has(header) && candidates.has(foldImportHeader(header))
    );
    if (!match) continue;
    mapping[field.key] = match;
    used.add(match);
  }

  return mapping;
};

export const compactColumnMapping = (
  mapping: Record<string, string>
): Record<string, string> =>
  Object.fromEntries(Object.entries(mapping).filter(([, value]) => value.trim().length > 0));

export const isColumnMappingComplete = (
  fields: readonly SuggestColumnField[],
  mapping: Record<string, string>
): boolean => {
  const requiredOk = fields
    .filter((field) => field.required === true)
    .every((field) => Boolean(mapping[field.key]));
  if (!requiredOk) return false;

  const subjectFields = fields.filter((field) => field.required === 'subject');
  if (subjectFields.length > 0 && !subjectFields.some((field) => mapping[field.key])) {
    return false;
  }

  const sources = Object.values(mapping).filter(Boolean);
  return new Set(sources).size === sources.length;
};
